import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField, SelectField, TextAreaField } from '../../components/ui/Field.jsx';
import { WeightInput } from '../../components/ui/WeightInput.jsx';
import { Modal } from '../../components/ui/Modal.jsx';

const ISSUED_TO_LABEL = { karigar: 'Aasari', employee: 'Employee', customer: 'Customer' };

function today() {
  return new Date().toISOString().slice(0, 10);
}

function newItemRow() {
  return {
    key: crypto.randomUUID(),
    itemName: '',
    serialNumber: '',
    purityId: '',
    purityLabel: '',
    grossWeight: '0',
    netWeight: '0',
    quantity: '1',
    remarks: '',
  };
}

export default function IssueForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [voucherNo, setVoucherNo] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [issuedToType, setIssuedToType] = useState('karigar');
  const [karigarId, setKarigarId] = useState('');
  const [issuedToName, setIssuedToName] = useState('');
  const [issuedToAddress, setIssuedToAddress] = useState('');
  const [purpose, setPurpose] = useState('');
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [items, setItems] = useState([newItemRow()]);
  const [remarks, setRemarks] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [karigarModalOpen, setKarigarModalOpen] = useState(false);
  const [newKarigar, setNewKarigar] = useState({ name: '', mobile: '' });

  const karigarsQuery = useQuery({ queryKey: ['masters', 'suppliers', 'karigar'], queryFn: () => api.get('/masters/suppliers', { query: { type: 'karigar' } }) });
  const puritiesQuery = useQuery({ queryKey: ['masters', 'purities'], queryFn: () => api.get('/masters/purities') });

  const applyItemPurity = (key, id) => {
    const purity = (puritiesQuery.data ?? []).find((p) => String(p.id) === id);
    updateItem(key, { purityId: id, purityLabel: purity ? `${purity.metal} ${purity.label}` : '' });
  };

  const addKarigarMutation = useMutation({
    mutationFn: () => api.post('/masters/suppliers', { name: newKarigar.name, supplierType: 'karigar', mobile: newKarigar.mobile || undefined }),
    onSuccess: (supplier) => {
      queryClient.invalidateQueries({ queryKey: ['masters', 'suppliers', 'karigar'] });
      setKarigarId(String(supplier.id));
      setKarigarModalOpen(false);
      setNewKarigar({ name: '', mobile: '' });
    },
  });

  const selectedKarigar = (karigarsQuery.data ?? []).find((k) => String(k.id) === karigarId);

  const updateItem = (key, patch) => setItems((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeItem = (key) => setItems((rows) => (rows.length > 1 ? rows.filter((r) => r.key !== key) : rows));
  const addItem = () => setItems((rows) => [...rows, newItemRow()]);

  const totalNetWeight = items.reduce((acc, i) => acc + Number(i.netWeight || 0), 0);
  const totalQuantity = items.reduce((acc, i) => acc + Number(i.quantity || 0), 0);

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/issue/create', payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      navigate(`/issue-receipt/${result.issue_id}`, { state: { justCreated: true } });
    },
    onError: (err) => {
      if (err instanceof ApiClientError && err.details) setFieldErrors(err.details);
      setSubmitError(err instanceof ApiClientError ? err.message : 'Could not save this voucher. Please try again.');
    },
  });

  const effectiveIssuedToName = issuedToType === 'karigar' ? selectedKarigar?.name ?? issuedToName : issuedToName;

  const canSubmit =
    !!issueDate &&
    effectiveIssuedToName.trim().length >= 2 &&
    issuedToAddress.trim().length >= 3 &&
    items.every((i) => i.itemName.trim() && i.purityLabel.trim() && Number(i.quantity) >= 1);

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    setFieldErrors({});
    createMutation.mutate({
      voucherNo: voucherNo.trim() || undefined,
      issueDate,
      issuedToType,
      issuedToId: issuedToType === 'karigar' && karigarId ? Number(karigarId) : undefined,
      issuedToName: effectiveIssuedToName,
      issuedToAddress,
      purpose: purpose || undefined,
      expectedReturnDate: expectedReturnDate || undefined,
      remarks: remarks || undefined,
      items: items.map((item) => ({
        itemName: item.itemName,
        serialNumber: item.serialNumber || undefined,
        purityId: item.purityId ? Number(item.purityId) : undefined,
        purityLabel: item.purityLabel,
        grossWeight: Number(item.grossWeight),
        netWeight: Number(item.netWeight),
        quantity: Number(item.quantity),
        remarks: item.remarks || undefined,
      })),
    });
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5 pb-28">
      <h1 className="font-display text-2xl font-semibold tracking-tight">New issue voucher</h1>

      <section className="card grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
        <TextField
          label="Voucher number"
          value={voucherNo}
          onChange={(e) => setVoucherNo(e.target.value)}
          hint="Leave blank to auto-generate, or type the number from your challan book"
        />
        <TextField label="Date of issue" type="date" required value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
      </section>

      <section className="card flex flex-col gap-4 p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Issued to</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SelectField
            label="Type"
            value={issuedToType}
            onChange={(e) => {
              setIssuedToType(e.target.value);
              setKarigarId('');
              setIssuedToName('');
            }}
          >
            {Object.entries(ISSUED_TO_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </SelectField>

          {issuedToType === 'karigar' ? (
            <div>
              <SelectField label="Aasari" value={karigarId} onChange={(e) => setKarigarId(e.target.value)}>
                <option value="">Select an aasari</option>
                {(karigarsQuery.data ?? []).map((k) => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </SelectField>
              <button type="button" onClick={() => setKarigarModalOpen(true)} className="mt-1.5 text-sm text-primary hover:underline">
                + Add a new aasari
              </button>
            </div>
          ) : (
            <TextField label="Name" required value={issuedToName} onChange={(e) => setIssuedToName(e.target.value)} />
          )}
        </div>
        <TextField label="Address" required value={issuedToAddress} onChange={(e) => setIssuedToAddress(e.target.value)} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField label="Purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} hint="E.g. polishing, stone setting, resizing" />
          <TextField label="Expected return date" type="date" value={expectedReturnDate} onChange={(e) => setExpectedReturnDate(e.target.value)} />
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Items issued</h2>
          <Button type="button" variant="secondary" size="sm" onClick={addItem}>
            <Plus size={15} aria-hidden="true" /> Add item
          </Button>
        </div>
        {items.map((item) => (
          <div key={item.key} className="card flex flex-col gap-3 p-4">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <TextField label="Item name" required value={item.itemName} onChange={(e) => updateItem(item.key, { itemName: e.target.value })} />
              </div>
              {items.length > 1 ? (
                <button type="button" onClick={() => removeItem(item.key)} className="mt-7 flex h-touch w-touch shrink-0 items-center justify-center rounded-card text-muted hover:bg-danger/10 hover:text-danger" aria-label="Remove item">
                  <Trash2 size={18} aria-hidden="true" />
                </button>
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField label="Serial number" value={item.serialNumber} onChange={(e) => updateItem(item.key, { serialNumber: e.target.value })} />
              <TextField label="Quantity" type="number" inputMode="numeric" min="1" value={item.quantity} onChange={(e) => updateItem(item.key, { quantity: e.target.value })} />
            </div>
            <SelectField label="Purity of gold / silver" required value={item.purityId} onChange={(e) => applyItemPurity(item.key, e.target.value)}>
              <option value="">Select purity</option>
              {(puritiesQuery.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.metal} {p.label}</option>
              ))}
            </SelectField>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <WeightInput label="Gross weight" value={item.grossWeight} onChange={(v) => updateItem(item.key, { grossWeight: v })} />
              <WeightInput label="Net weight" value={item.netWeight} onChange={(v) => updateItem(item.key, { netWeight: v })} />
            </div>
            <TextField label="Remarks" value={item.remarks} onChange={(e) => updateItem(item.key, { remarks: e.target.value })} />
          </div>
        ))}
      </section>

      <TextAreaField label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />

      <section className="card ml-auto w-full max-w-sm p-5">
        <div className="flex justify-between text-sm">
          <span className="text-muted">Total quantity</span>
          <span className="tabular font-semibold">{totalQuantity}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted">Total net weight</span>
          <span className="tabular font-semibold">{formatWeight(totalNetWeight)}</span>
        </div>
      </section>

      {submitError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {submitError}
        </p>
      ) : null}

      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center justify-end gap-2 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        <Button type="submit" size="lg" disabled={!canSubmit} loading={createMutation.isPending}>
          <CheckCircle2 size={18} aria-hidden="true" /> Save voucher
        </Button>
      </div>

      <Modal open={karigarModalOpen} onClose={() => setKarigarModalOpen(false)} title="Add an Aasari">
        <div className="flex flex-col gap-4">
          <TextField label="Name" required value={newKarigar.name} onChange={(e) => setNewKarigar((k) => ({ ...k, name: e.target.value }))} />
          <TextField label="Mobile" inputMode="numeric" value={newKarigar.mobile} onChange={(e) => setNewKarigar((k) => ({ ...k, mobile: e.target.value }))} />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setKarigarModalOpen(false)}>Cancel</Button>
            <Button type="button" disabled={!newKarigar.name.trim()} loading={addKarigarMutation.isPending} onClick={() => addKarigarMutation.mutate()}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </form>
  );
}
